import { useForm } from "react-hook-form";
import { QUESTION_TYPE } from "../../utils/constants";
import {
  CardInformation,
  EcReturn,
  PurchaseEcReturnFormValues,
  Questionnaire,
  SocialProfile,
  DiscountSetting,
} from "../../types/pages/EcWebsettlement/purchase_ec_return";

export const usePurchaseEcReturnUtilities = (
  paymentToken: string,
  ecReturn: EcReturn,
  commission: number,
  current_email: string,
  socialProfile?: SocialProfile,
  registeredCard?: CardInformation | null,
  questionnaire?: Questionnaire,
  discount_setting?: DiscountSetting,
  is_target_for_discount?: boolean,
) => {
  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PurchaseEcReturnFormValues>({
    mode: "all",
    defaultValues: {
      number: 1,
      is_terms_checked: true,
      answers: ecReturn ? ecReturn.ec_return_questions.filter(question => question.question_type !== QUESTION_TYPE.NONE).map((question, index) => {
        if (ecReturn.is_auction) {
          const previousAnswer = ecReturn.previous_return_answers?.[index] || "";
          if (question.question_type === QUESTION_TYPE.SELECT) {
            return question.ec_return_question_question_answers.some(answer => answer.answer === previousAnswer)
              ? previousAnswer
              : "";
          }
          return previousAnswer;
        }
        if(question.question_type == QUESTION_TYPE.SELECT){
          return question.ec_return_question_question_answers[0].answer
        } else { return '' }}) : [],
      checked_sku_id: ecReturn.ec_return_skus.length > 0 ? ecReturn.ec_return_skus.filter((sku) => sku.remaining_number > 0).shift().id : null,
      additional_amount: ecReturn.is_auction
        ? (ecReturn.auction_minimum_next_bid ?? ecReturn.price).toString()
        : "",
      last_name: socialProfile?.last_name || "",
      first_name: socialProfile?.first_name || "",
      postal_code: socialProfile?.postal_code || "",
      full_address: socialProfile?.full_address || "",
      phone_number: socialProfile?.phone_number || "",
      email: current_email,
      card_brand: registeredCard?.brand,
      card_last4: registeredCard?.last4,
      card_exp_year: registeredCard?.exp_year,
      card_exp_month: registeredCard?.exp_month,
      questionnaire_id: questionnaire?.id,
      questionnaire_option_id: null,
      questionnaire_sentence: null,
      destination_reason_text: null,
      payment_token: paymentToken,
      payment_method: "credit_card",
      article_message: "応援しています。頑張ってください。",
      commission: commission,
      conveni_code: null,
      conveni_tel: null,
      conveni_phonetic_full_name: "",
      quick_bid: false
    },
  });

  const variableBgColor = (withError) => {
    if (withError) {
      return "bg-red-100";
    } else {
      return "bg-blue-200";
    }
  };

  const cardInformation = (): CardInformation => {
    return {
      brand: watch('card_brand'),
      last4: watch('card_last4'),
      exp_year: watch('card_exp_year'),
      exp_month: watch('card_exp_month'),
    }
  }

  const numerableAdditionalAmount = () => {
    return parseInt(watch("additional_amount") || "0");
  };

  const priceByAmount = () => {
    return ecReturn.price * Number(watch("number"))
  }

  const targetDiscountAmount = () => {
    if (!is_target_for_discount) {
      return 0;
    }

    let discountAmount = 0;
    if (discount_setting?.discount_type === "introduction") {
      discountAmount = watch("appliedCoupon")?.amount ?? 0;
    } else {
      discountAmount = discount_setting?.discount_amount || 0;
    }
    return discountAmount;
  };

  const totalAmount = () => {
    if (ecReturn.is_auction) {
      // オークションでは口数・上乗せ・クーポンは適用せず、入札額(=additional_amount)のみが対象
      return Number(watch("additional_amount")) + commission;
    }
    const total = priceByAmount() + Number(watch("additional_amount")) + commission;
    return total - targetDiscountAmount();
  };

  const amountDetailText = () => {
    if (numerableAdditionalAmount() > 0) {
      return `(${ecReturn.price.toLocaleString()}円 × ${Number(watch(
        "number"
      )).toLocaleString()}口 + 上乗せ支援${numerableAdditionalAmount().toLocaleString()}円)`;
    } else {
      return `(${ecReturn.price.toLocaleString()}円 × ${Number(watch(
        "number"
      )).toLocaleString()}口)`;
    }
  };

  return {
    control,
    cardInformation,
    variableBgColor,
    setValue,
    getValues,
    watch,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
    numerableAdditionalAmount,
    totalAmount,
    priceByAmount,
    amountDetailText,
    targetDiscountAmount,
  };
};
